import * as React from 'react';
import * as PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Popup } from 'semantic-ui-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import moment from 'moment';
import { BaseInputProps, OptionalBaseInputProps } from './types/BaseInputProps';
import Constants from '../utils/Constants';
import { InputType } from './types/InputType';
import { BaseInputState } from './types/BaseInputState';
import { ButtonType } from '../types/ButtonType';
import DateUtils from '../dateutils/DateUtils';
import { TimeInputType } from '../timeinput/types/TimeInputType';
import styleMap from '../styleMap';
import { StrictValueType } from './types/ValueType';
import ButtonUtils from '../utils/ButtonUtils';

export default abstract class BaseInput<Props extends BaseInputProps> extends React.Component<
  Props,
  BaseInputState
> {
  static propTypes = {
    value: PropTypes.instanceOf(Date).isRequired,
    onChange: PropTypes.func.isRequired,
    buttonPlacement: PropTypes.oneOf(['outsideOfInputs', 'inlineWithInputs']),
    doubleClickDelayInMillis: PropTypes.number,
    focusColor: PropTypes.string,
    showTooltipDelayInMillis: PropTypes.number,
    showTooltips: PropTypes.bool,
    size: PropTypes.oneOf(['mini', 'small', 'large', 'big', 'huge', 'massive']),
    valueType: PropTypes.oneOf(['start', 'end', 'unspecified'])
  };

  static defaultProps: OptionalBaseInputProps = {
    buttonPlacement: 'outsideOfInputs',
    focusColor: '#85b7d9',
    doubleClickDelayInMillis: Constants.DOUBLE_CLICK_DELAY_IN_MILLIS,
    showTooltipDelayInMillis: Constants.SHOW_TOOLTIP_DELAY_IN_MILLIS,
    showTooltips: true,
    size: 'small',
    valueType: 'start'
  };

  state = {
    focusedInputType: 'hour' as InputType
  };

  abstract getTimeoutIDMap(): object;

  abstract getButtonSingleClickStepCount(inputType: InputType): number;

  abstract getButtonDoubleClickStepCount(inputType: InputType): number;

  decrementOrIncrementValue = (
    buttonType: ButtonType,
    inputType: InputType,
    hasDoubleClicksEnabled = true
  ) => {
    const { value } = this.props;
    const currentValue = moment(value).get(inputType);
    const timeoutID = this.getTimeoutIDMap()[`${buttonType} ${inputType}`];

    if (timeoutID) {
      window.clearInterval(timeoutID);
      this.handleDoubleClick(buttonType, inputType, currentValue);
    } else if (hasDoubleClicksEnabled && this.hasDoubleClicksEnabled(inputType)) {
      this.handleDelayedSingleClick(buttonType, inputType, currentValue);
    } else {
      this.handleSingleClick(buttonType, inputType, currentValue);
    }
  };

  handleDoubleClick = (buttonType: ButtonType, inputType: InputType, currentValue: number) => {
    const stepCount = this.getButtonDoubleClickStepCount(inputType);
    this.setNewValue(buttonType, inputType, currentValue, stepCount);
  };

  hasDoubleClicksEnabled = (inputType: InputType) => {
    const doubleClickStepCount = this.getButtonDoubleClickStepCount(inputType);
    return doubleClickStepCount > 0;
  };

  handleDelayedSingleClick = (buttonType: ButtonType, inputType: InputType, currentValue: number) => {
    const { doubleClickDelayInMillis } = this.props;

    const newTimeoutID = window.setTimeout(() => {
      this.handleSingleClick(buttonType, inputType, currentValue);
    }, doubleClickDelayInMillis);

    this.setTimeoutID(newTimeoutID, `${buttonType} ${inputType}`);
  };

  handleSingleClick = (buttonType: ButtonType, inputType: InputType, currentValue: number) => {
    const stepCount = this.getButtonSingleClickStepCount(inputType);
    this.setNewValue(buttonType, inputType, currentValue, stepCount);
  };

  setNewValue = (buttonType: ButtonType, inputType: InputType, currentValue: number, stepCount: number) => {
    const { onChange, value } = this.props;
    const newInputValue = DateUtils.getNewInputValue(buttonType, inputType, currentValue, stepCount, value);

    onChange(DateUtils.getNewValue(value, newInputValue, inputType));
    this.setTimeoutID(0, `${buttonType} ${inputType}`);
  };

  setTimeoutID = (timeoutID: number, timeoutIDName: string) => {
    this.getTimeoutIDMap()[timeoutIDName] = timeoutID;
  };

  changeValue = (newInputValueStr: string, inputType: InputType) => {
    const { onChange, value } = this.props;
    const newInputValue = parseInt(newInputValueStr, 10);

    if (
      newInputValue >= DateUtils.getMinValue(inputType) &&
      newInputValue <= DateUtils.getMaxValue(inputType, value)
    ) {
      onChange(DateUtils.getNewValue(value, newInputValue, inputType));
    }
  };

  onInputFocus = (focusedInputType: InputType) => {
    this.setState({
      focusedInputType
    });
  };

  resetValue = (inputType: InputType) => {
    const { valueType, value } = this.props;

    if (valueType !== 'unspecified') {
      const resetValue =
        valueType === 'start' ? DateUtils.getMinValue(inputType) : DateUtils.getMaxValue(inputType, value);

      this.resetToValue(resetValue, inputType);
    }
  };

  resetToValue = (resetValue: number, inputType: InputType) => {
    const { onChange, value } = this.props;
    onChange(
      moment(value)
        .set(inputType, resetValue)
        .toDate()
    );
  };

  onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, inputType: InputType) => {
    const { value } = this.props;

    event.preventDefault();
    event.stopPropagation();

    switch (event.key) {
      case 'ArrowUp':
      case '+':
        if (event.ctrlKey) {
          _.times(2, () => this.decrementOrIncrementValue('increment', inputType));
        } else {
          this.decrementOrIncrementValue('increment', inputType, false);
        }
        break;

      case 'ArrowDown':
      case '-':
        if (event.ctrlKey) {
          _.times(2, () => this.decrementOrIncrementValue('decrement', inputType));
        } else {
          this.decrementOrIncrementValue('decrement', inputType, false);
        }
        break;

      case 'PageUp':
        _.times(2, () => this.decrementOrIncrementValue('increment', inputType));
        break;

      case 'PageDown':
        _.times(2, () => this.decrementOrIncrementValue('decrement', inputType));
        break;

      case 'Home':
        this.resetToValue(DateUtils.getMinValue(inputType), inputType);
        break;

      case 'End':
        this.resetToValue(DateUtils.getMaxValue(inputType, value), inputType);
        break;

      default:
    }
  };

  getInputComponent = (inputType: TimeInputType, additionalStyleMap: object = {}): React.ReactElement => {
    const {
      buttonPlacement,
      focusColor,
      showTooltipDelayInMillis,
      showTooltips,
      size,
      value,
      valueType
    } = this.props;

    const { focusedInputType } = this.state;

    const inputStyle = {
      ...styleMap.common.input,
      ...styleMap[buttonPlacement].input,
      ...additionalStyleMap
    };

    if (focusedInputType === inputType && focusColor) {
      inputStyle.color = focusColor;
      inputStyle.fontWeight = 'bold';
    }

    const inputComponent = (
      <div className={`ui input ${size}`}>
        <input
          type="text"
          style={inputStyle}
          maxLength={3}
          value={DateUtils.getInputValueAsString(moment(value).get(inputType), inputType)}
          onDoubleClick={() => this.resetValue(inputType)}
          onChange={
            (event: React.ChangeEvent<HTMLInputElement>) => this.changeValue(event.target.value, inputType)
            // eslint-disable-next-line react/jsx-curly-newline
          }
          onFocus={() => this.onInputFocus(inputType)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => this.onKeyDown(event, inputType)}
        />
      </div>
    );

    if (showTooltips && valueType !== 'unspecified') {
      const inputResetValue = DateUtils.getInputResetValue(value, valueType as StrictValueType, inputType);
      const tooltipText = `Double-click to set to ${inputResetValue}`;

      return (
        <Popup
          content={tooltipText}
          mouseEnterDelay={showTooltipDelayInMillis}
          mouseLeaveDelay={Constants.HIDE_TOOLTIP_DELAY_IN_MILLIS}
          on="hover"
          trigger={inputComponent}
        />
      );
    }

    return inputComponent;
  };

  getButtonComponent = (buttonType: ButtonType, possibleInputType?: InputType): React.ReactElement => {
    const { buttonPlacement, showTooltipDelayInMillis, showTooltips, size, value } = this.props;
    const { focusedInputType } = this.state;
    const inputType = possibleInputType || focusedInputType;
    const buttonSingleClickStepCount = this.getButtonSingleClickStepCount(inputType);

    const buttonStyle = {
      ...styleMap[buttonPlacement].button.base,
      ...styleMap[buttonPlacement].button[buttonType]
    };

    const buttonComponent = (
      <Button
        size={size}
        style={buttonStyle}
        icon={ButtonUtils.getButtonIconName(buttonType, buttonPlacement)}
        onClick={() => this.decrementOrIncrementValue(buttonType, inputType)}
        disabled={!ButtonUtils.isEnabledButton(value, buttonType, inputType, buttonSingleClickStepCount)}
      />
    );

    if (showTooltips) {
      const buttonDoubleClickStepCount = this.getButtonDoubleClickStepCount(inputType);
      const tooltipText = `Double-click to ${buttonType} by ${buttonDoubleClickStepCount}`;
      return (
        <Popup
          content={tooltipText}
          mouseEnterDelay={showTooltipDelayInMillis}
          mouseLeaveDelay={Constants.HIDE_TOOLTIP_DELAY_IN_MILLIS}
          on="hover"
          trigger={buttonComponent}
        />
      );
    }

    return buttonComponent;
  };
}
